import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import D3Loader from './3d-loader';
import { ErrorBoundary } from './error-boundary';
import { Center, Environment, Html, useHelper } from '@react-three/drei';
import { useBundleStore } from '../hooks/bundle-store';
import RotationIcon from './rotation-icon';
import SunButton from '../components/sun-button';
import ModelList from './model-list';
import ResetButton from '../components/reset-button';
import { useShallow } from 'zustand/react/shallow';
import { Group, SpotLightHelper, Vector3 } from 'three';
import { computeBoundingBox } from './camera';
import { RotationController } from './rotation-controller';
import RotationButton from '../components/rotation-btn';

function ThreeDDesign() {
  const canvasParent = useRef<HTMLDivElement>(null);
  const cameraControlRef = useRef(null);
  const modelsRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const { showBackLight, isApparel, isSceneRotationEnabled } = useBundleStore(
    useShallow((state) => ({
      showBackLight: state?.showBackLight,
      isApparel: state?.bundle?.isApparel,
      isSceneRotationEnabled: state?.isSceneRotationEnabled,
    })),
  );

  useEffect(() => {
    document.body.style.cursor = isHovered ? 'move' : 'auto';
  }, [isHovered]);

  return (
    <div ref={canvasParent} style={{ height: '100%', width: '100%', position: 'relative' }}>
      <Canvas
        shadows
        onPointerOver={() => setIsHovered(true)}
        onPointerOut={() => setIsHovered(false)}>
        <color attach="background" args={['#fafafa']} />
        {isApparel ? (
          <>
            <Environment preset="sunset" environmentIntensity={0.8} />
            <Lighting modelsRef={modelsRef} />
          </>
        ) : (
          <ambientLight intensity={2.9} />
        )}
        <ErrorBoundary
          fallback={() => {
            return (
              <Html center>
                <div style={{ width: '200px', textAlign: 'center' }}>
                  <div style={{ color: 'red' }}>Something went wrong</div>
                </div>
              </Html>
            );
          }}>
          <Suspense fallback={<D3Loader />}>
            <ModelList
              canvasParent={canvasParent.current}
              cameraControlRef={cameraControlRef}
              modelsRef={modelsRef}
            />

            {isApparel && (
              <RotationController target={modelsRef} isPlaying={isSceneRotationEnabled} />
            )}
          </Suspense>
        </ErrorBoundary>
      </Canvas>
      <RotationIcon />

      {showBackLight && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 5,
          }}>
          <SunButton />
        </div>
      )}
      <ResetButton cameraControlRef={cameraControlRef.current} modelsRef={modelsRef.current} />
      <RotationButton />
    </div>
  );
}

export const ThreeDDesignLoader = React.memo(ThreeDDesign);

function Lighting({ modelsRef }: any) {
  const keyLightRef = useRef(null);
  const fillLightRef = useRef(null);
  const LightRef = useRef(null);
  // //@ts-ignore
  // useHelper(keyLightRef, SpotLightHelper, 'red');
  // //@ts-ignore
  // useHelper(fillLightRef, SpotLightHelper, 'blue');
  // //@ts-ignore
  // useHelper(LightRef, SpotLightHelper, 'green');
  const boundingBox = modelsRef.current
    ? computeBoundingBox(modelsRef.current as Group)
    : undefined;

  const center = boundingBox ? boundingBox.getCenter(new Vector3()) : undefined;
  return (
    <>
      <ambientLight intensity={0.4} />

      <spotLight
        //@ts-ignore
        ref={keyLightRef}
        position={[5, 5, 5]}
        intensity={5} // Note: Spotlight intensity values are often higher
        angle={0.6}
        penumbra={0.1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0001}
        target-position={center ? center : [0, 1, 0]} // Make it point at the chest area of a typical model
      />

      <spotLight
        //@ts-ignore
        ref={fillLightRef}
        position={[-5, 5, 5]}
        intensity={20}
        angle={0.6}
        penumbra={0.1}
        target-position={center ? center : [0, 1, 0]} // Make it point at the chest area of a typical model
      />

      <spotLight
        //@ts-ignore
        ref={LightRef}
        position={[0, 3, -5]}
        intensity={15}
        angle={0.8}
        penumbra={0.5}
        target-position={center ? center : [0, 1, 0]} // Make it point at the chest area of a typical model
      />
    </>
  );
}
